import { memo, useMemo } from 'react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  LabelList,
  Tooltip as ChartTooltip,
} from 'recharts';
import { Empty, Skeleton } from 'antd';
import { Panel } from './Panel';
import { useWidgetData, groupByCategory, accentMap, currencyFmt, compactFmt } from './shared';
import type { DashboardWidget } from '../../types/panelConfig';

interface Props {
  widget: Extract<DashboardWidget, { type: 'chart-line' }>;
  sourceId: number | null | undefined;
  parameters?: Record<string, string>;
}

// ISO date / datetime → "dd.MM"; anything else (e.g. hourly "9", "09:00") as-is.
function formatCategory(name: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(name);
  if (m) return `${m[3]}.${m[2]}`;
  return name.length > 10 ? name.slice(5) : name;
}

function LineChartWidgetImpl({ widget, sourceId, parameters }: Props) {
  const { data, isLoading } = useWidgetData(sourceId, `chart-line-${widget.reportCode}`, parameters);
  const { categoryField, valueField, aggregation = 'sum', title, subtitle, color, showLabels } = widget.config;

  const stroke = typeof color === 'string' ? accentMap[color] ?? accentMap.blue : accentMap.blue;
  // Tooltip series name — show the metric label (e.g. "Net ciro") instead of
  // the raw dataKey "value".
  const valueName = subtitle ?? title ?? 'Değer';

  const series = useMemo(() => {
    const rows = data ?? [];
    const grouped = groupByCategory(rows, categoryField, valueField, aggregation);
    return grouped
      .sort((a, b) => {
        // Numeric categories (e.g. hour buckets 0..23) sort numerically;
        // ISO date strings fall back to lexical (== chronological).
        const na = Number(a.name);
        const nb = Number(b.name);
        if (a.name !== '' && b.name !== '' && !Number.isNaN(na) && !Number.isNaN(nb)) return na - nb;
        return a.name.localeCompare(b.name);
      })
      .slice(-31)
      .map((r) => ({ category: formatCategory(r.name), value: r.value }));
  }, [data, categoryField, valueField, aggregation]);

  return (
    <div data-testid="chart-line-widget">
      <Panel title={title} subtitle={subtitle}>
        {isLoading ? (
          <Skeleton.Node active style={{ width: '100%', height: 260 }} />
        ) : series.length === 0 ? (
          <Empty description="Veri yok" style={{ padding: 40 }} />
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={series} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="2 4" stroke="var(--color-border)" vertical={false} />
              <XAxis
                dataKey="category"
                tick={{ fontSize: 11, fill: 'var(--color-text-muted)' }}
                axisLine={{ stroke: 'var(--color-border)' }}
                tickLine={false}
              />
              <YAxis
                tick={{ fontSize: 11, fill: 'var(--color-text-muted)' }}
                axisLine={false}
                tickLine={false}
                tickFormatter={(v) => compactFmt.format(v as number)}
                width={48}
              />
              <ChartTooltip
                formatter={(v) => currencyFmt.format(Number(v))}
                contentStyle={{
                  borderRadius: 4,
                  border: '1px solid var(--color-border)',
                  fontSize: 12,
                  padding: '6px 10px',
                  boxShadow: '0 4px 16px var(--shadow-color-strong)',
                }}
                labelStyle={{ fontWeight: 600, color: 'var(--color-text-primary)' }}
              />
              <Line
                type="monotone"
                dataKey="value"
                name={valueName}
                stroke={stroke}
                strokeWidth={1.5}
                dot={showLabels ? { r: 2.5, fill: stroke } : false}
                activeDot={{ r: 4, fill: stroke }}
              >
                {showLabels && (
                  <LabelList
                    dataKey="value"
                    position="top"
                    offset={8}
                    formatter={(v) => compactFmt.format(Number(v) || 0)}
                    style={{ fontSize: 10, fill: 'var(--color-text-muted)', fontWeight: 600 }}
                  />
                )}
              </Line>
            </LineChart>
          </ResponsiveContainer>
        )}
      </Panel>
    </div>
  );
}

export const LineChartWidget = memo(LineChartWidgetImpl);
